import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { runLoginFlow, type Credentials } from './flowExecutor';
import { probeSession } from './session';
import { isKnownPath } from '../navigation/routes';

/**
 * 'checking'      probing Authentik for an existing session (cold load)
 * 'authenticated' render the app
 * 'anonymous'     Authentik said there is no session -> login form
 * 'unavailable'   we could NOT ask (network error / 5xx / non-JSON) ->
 *                 "couldn't reach the sign-in service" + Retry. Deliberately
 *                 distinct from 'anonymous': "couldn't check" is not "not
 *                 logged in", and showing the login form for it was the
 *                 intermittent refresh-to-login bug.
 * 'redirecting'   the browser is being sent to Authentik (login flow)
 */
export type AuthStatus = 'checking' | 'authenticated' | 'anonymous' | 'unavailable' | 'redirecting';

interface AuthContextValue {
  status: AuthStatus;
  error: string | null;
  login: (creds: Credentials) => Promise<void>;
  /** Re-run the session probe (the 'unavailable' state's Retry button). */
  retry: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Captured ONCE at module load, before any effect runs, because the effect
// below rewrites the URL on the callback path. Reading window.location
// inside the effect instead would make it non-idempotent: React StrictMode
// (dev) runs mount effects twice, and the second run would see the rewritten
// URL — no longer a callback, no ?skipAuth either — and fall through to the
// session probe, bouncing a freshly logged-in visitor to the login form.
const LANDED_VIA_CALLBACK = window.location.pathname === '/callback';
const DEV_SKIP_AUTH =
  import.meta.env.DEV && new URLSearchParams(window.location.search).get('skipAuth') !== null;

// Where the visitor was trying to go when they turned out to be logged
// out. Remembered across the Authentik round-trip so /callback can put them
// back there (e.g. a refresh on /map/general while the session had expired
// — without this, the second half of the "refresh loses my place" bug).
// sessionStorage: per-tab, gone when the tab closes, never persisted.
const INTENDED_PATH_KEY = 'maps.intendedPath';

function rememberIntendedPath() {
  const { pathname, search } = window.location;
  if (pathname === '/callback' || !isKnownPath(pathname)) return;
  try {
    sessionStorage.setItem(INTENDED_PATH_KEY, pathname + search);
  } catch {
    // storage unavailable (private mode quotas etc.) — just land on '/'
  }
}

function takeIntendedPath(): string {
  try {
    const stored = sessionStorage.getItem(INTENDED_PATH_KEY);
    sessionStorage.removeItem(INTENDED_PATH_KEY);
    if (stored) {
      const path = stored.split('?')[0];
      if (isKnownPath(path)) return stored;
    }
  } catch {
    // ignore
  }
  return '/';
}

// This app never sees a token here — same as design/hub/hub.html, this is a
// login trigger, not a full OIDC relying-party integration. Once Authentik's
// own redirect lands the browser back on this origin's callback route with
// the session cookie set, the app is "authenticated" for UI purposes; real
// access control still lives server-side (GeoServer CORS + Caddy), same
// trust model as the hub (docs/deployment.md, v0.3.3).
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [error, setError] = useState<string | null>(null);
  const [probeAttempt, setProbeAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    // /callback lands here mid-flow with authentik_session already set by
    // the redirect chain; treat that as authenticated without re-probing.
    // Then rewrite the URL (dropping the OAuth ?code=&state= params) to
    // wherever the visitor was originally headed — or '/' — so a refresh is
    // a normal cold load and the address bar never sits on /callback.
    // NavigationProvider mounts after this and reads the rewritten path.
    if (LANDED_VIA_CALLBACK) {
      if (window.location.pathname === '/callback') window.history.replaceState(null, '', takeIntendedPath());
      setStatus('authenticated');
      return;
    }
    // TEMPORARY, review-only: bypass auth with ?skipAuth=1 so the map UI can
    // be visually reviewed without a live Authentik/Caddy /idp proxy. Not
    // part of the deliverable — remove before deploying.
    if (DEV_SKIP_AUTH) {
      setStatus('authenticated');
      return;
    }

    // A visitor already logged into hub/erp/farm shares the Authentik
    // session via AUTHENTIK_COOKIE_DOMAIN=abrarfarms.com, so probe first
    // instead of always showing the form (cross-app SSO, no re-prompt).
    // Three outcomes, three distinct UI states — see session.ts.
    setStatus('checking');
    probeSession().then((result) => {
      if (cancelled) return;
      if (result === 'anonymous') rememberIntendedPath();
      setStatus(result);
    });
    return () => {
      cancelled = true;
    };
  }, [probeAttempt]);

  const retry = useCallback(() => setProbeAttempt((n) => n + 1), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      error,
      retry,
      async login(creds: Credentials) {
        setError(null);
        try {
          const outcome = await runLoginFlow(creds);
          setStatus('redirecting');
          window.location.href = outcome.url;
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
        }
      },
    }),
    [status, error, retry],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
